import React, { useState } from 'react';
import styled from 'styled-components';
import { Breakpoints, Grid } from '../../../app/StyleGuide';
import ErrorMessage from '../../../components/ErrorMessage';
import LoadingState from '../../../components/LoadingState';
import Overlay from '../../../components/Overlay';
import { useConnectChurchAccountModal } from '../hooks/useConnectChurchAccountModal';
import { ChurchAccount } from '../models/churchAccount';
import { ConnectChurchAccountConfirmationScreen } from './ConnectChurchAccountConfirmationScreen';
import { ConnectChurchAccountCreateScreen } from './ConnectChurchAccountCreateScreen';
import { ConnectChurchAccountSelectionScreen } from './ConnectChurchAccountSelectionScreen';
import { ConnectChurchAccountSuccessScreen } from './ConnectChurchAccountSuccessScreen';
import { ChurchAccountRedirect } from '../state/churchAccountRedirect';

const ModalContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: ${Grid._3};
  width: 100vw;
  height: 100vh;
  padding: ${Grid._10} ${Grid._5} ${Grid._5};
  text-align: center;

  @media screen and (min-width: ${Breakpoints.screen_sm}) {
    width: 640px;
    height: 600px;
    padding: ${Grid._8} ${Grid._7};
  }
`;

const StatusContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

type HeaderContent = {
  title: string;
  subtitle: string;
};

type ConnectChurchAccountModalProps = {
  preSelectedAccountId?: string;
  onSuccess: () => void;
  dismissHandler: () => void;
  redirect?: ChurchAccountRedirect;
  contentOverrides?: {
    create?: HeaderContent;
    confirmation?: HeaderContent;
  };
};

export const ConnectChurchAccountModal = ({
  preSelectedAccountId,
  onSuccess,
  dismissHandler,
  redirect = { action: 'link', redirectPath: window.location.hash },
  contentOverrides,
}: ConnectChurchAccountModalProps) => {
  const [screen, setScreen] = useState<'confirmation' | 'selection' | 'success'>('confirmation');
  const { state, submissionState, onSelectChurchAccount, onConnectChurchAccount } =
    useConnectChurchAccountModal(preSelectedAccountId);
  const isSubmitting = submissionState.status === 'submitting';
  const errorMessage = submissionState.status === 'error' ? submissionState.message : undefined;
  const disableConnectButton =
    isSubmitting || (submissionState.status === 'error' ? submissionState.disableButton : false);

  const onSelectDifferent = () => setScreen('selection');

  const handleSelectChurchAccount = (account: ChurchAccount) => {
    onSelectChurchAccount(account);
    setScreen('confirmation');
  };

  const handleConnectChurchAccount = () =>
    onConnectChurchAccount().then(didConnect => {
      if (didConnect) {
        setScreen('success');
        onSuccess();
      }
    });

  return (
    <Overlay dismissHandler={dismissHandler}>
      <ModalContent data-tracking-section="connect-church-account-modal">
        <Overlay.CloseButton data-tracking-id="sync-church-account-exit" />
        {state.status === 'error' && (
          <StatusContainer>
            <ErrorMessage>A problem occurred. Please try again.</ErrorMessage>
          </StatusContainer>
        )}

        {state.status === 'loading' && (
          <StatusContainer>
            <LoadingState />
          </StatusContainer>
        )}

        {state.status === 'ready' && state.selectedChurchAccount === undefined && (
          <ConnectChurchAccountCreateScreen
            org={state.org}
            churchAccountRedirect={redirect}
            title={contentOverrides?.create?.title}
            subtitle={contentOverrides?.create?.subtitle}
          />
        )}

        {state.status === 'ready' && state.selectedChurchAccount && (
          <>
            {screen === 'confirmation' ? (
              <ConnectChurchAccountConfirmationScreen
                org={state.org}
                selectedChurchAccount={state.selectedChurchAccount}
                isConnecting={isSubmitting}
                onSelectDifferent={onSelectDifferent}
                onConnectChurchAccount={handleConnectChurchAccount}
                errorMessage={errorMessage}
                disableConnectButton={disableConnectButton}
                title={contentOverrides?.confirmation?.title}
                subtitle={contentOverrides?.confirmation?.subtitle}
              />
            ) : screen === 'success' ? (
              <ConnectChurchAccountSuccessScreen dismissHandler={dismissHandler} />
            ) : (
              <ConnectChurchAccountSelectionScreen
                churchAccounts={state.churchAccounts}
                onSelectChurchAccount={handleSelectChurchAccount}
                churchAccountRedirect={redirect}
              />
            )}
          </>
        )}
      </ModalContent>
    </Overlay>
  );
};
